import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  LucideIcon,
  SunMedium,
  Wind,
  Cpu,
  Landmark,
  GraduationCap,
} from "lucide-react";

interface Solution {
  icon: LucideIcon;
  title: string;
  category: string;
  description: string;
  href: string;
}

interface SolutionsMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const SolutionsMenu = ({ isOpen, onClose }: SolutionsMenuProps) => {
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // UPDATED: Your real business solutions only
  const solutions: Solution[] = [
    // {
    //   icon: SunMedium,
    //   title: "Solar Monitoring Platform",
    //   category: "Renewable Energy",
    //   description:
    //     "Predictive remote solar plant monitoring with OPC UA integration, anomaly detection, and maintenance intelligence.",
    //   href: "/solutions/solar",
    // },
    // {
    //   icon: Wind,
    //   title: "Industrial IoT / Wind Integration",
    //   category: "Industrial IoT",
    //   description:
    //     "High-throughput OPC UA + Kafka architecture for real-time industrial telemetry, self-healing infrastructure, and open integration.",
    //   href: "/solutions/wind",
    // },
    {
      icon: Cpu,
      title: "Multi-Vendor Motor Synchronization",
      category: "Industrial Automation",
      description:
        "Unified OPC UA + TSN control layer for deterministic synchronization across multi-OEM motor ecosystems.",
      href: "/solutions/motor",
    },
    {
      icon: Landmark,
      title: "BFSI Microservices Modernization",
      category: "BFSI / FinTech",
      description:
        "Kafka-driven microservices, ElasticSearch tiering, and DC/DR failover for zero-downtime financial modernization.",
      href: "/solutions/bfsi",
    },
    {
      icon: GraduationCap,
      title: "EdTech Child-Safe Talent Platform",
      category: "EdTech / Mobile App",
      description:
        "Privacy-first Flutter platform for under-18 talent showcase, competitions, parental controls, and cognitive development.",
      href: "/solutions/edtech",
    },
  ];

  const handleSolutionClick = (solution: Solution) => {
    navigate(solution.href);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="fixed top-14 left-0 right-0 bg-background/95 backdrop-blur-md border-b border-border shadow-2xl z-[55]"
      onMouseLeave={onClose}
    >
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-foreground">Our Solutions</h3>
          <p className="text-muted-foreground mt-2 max-w-3xl">
            Purpose-built enterprise systems across renewable energy, industrial
            IoT, BFSI modernization, EdTech, and automation.
          </p>
        </div>

        {/* Mobile Layout */}
        <div className="grid grid-cols-1 gap-4 md:hidden max-h-[70vh] overflow-y-auto">
          {solutions.map((solution, index) => {
            const Icon = solution.icon;

            return (
              <button
                key={index}
                onClick={() => handleSolutionClick(solution)}
                className="w-full text-left group rounded-2xl border border-border bg-card hover:border-brand hover:shadow-lg transition-all duration-300 p-5"
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-xl ${index % 2 === 0 ? "bg-primary/10" : "bg-secondary/10"} flex-shrink-0 shadow-sm border ${index % 2 === 0 ? "border-primary/20" : "border-secondary/20"}`}
                  >
                    <Icon
                      className={`h-6 w-6 ${index % 2 === 0 ? "text-primary shadow-glow" : "text-secondary shadow-glow-secondary"}`}
                    />
                  </div>

                  <div className="flex-1">
                    <p
                      className={`text-xs uppercase tracking-wider ${index % 2 === 0 ? "text-primary shadow-glow" : "text-secondary shadow-glow-secondary"} font-semibold mb-2`}
                    >
                      {solution.category}
                    </p>

                    <h4 className="text-base font-semibold text-foreground group-hover:text-brand transition-colors mb-2">
                      {solution.title}
                    </h4>

                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {solution.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {solutions.map((solution, index) => {
            const Icon = solution.icon;

            return (
              <button
                key={index}
                onClick={() => handleSolutionClick(solution)}
                className="group text-left h-full rounded-3xl border border-border bg-card hover:border-brand hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 p-6"
              >
                {/* Icon */}
                <div
                  className={`flex h-14 w-14 items-center justify-center rounded-2xl ${index % 2 === 0 ? "bg-primary/10" : "bg-secondary/10"} mb-5 shadow-sm border ${index % 2 === 0 ? "border-primary/20" : "border-secondary/20"}`}
                >
                  <Icon
                    className={`h-7 w-7 ${index % 2 === 0 ? "text-primary shadow-glow" : "text-secondary shadow-glow-secondary"}`}
                  />
                </div>

                {/* Category */}
                {/* <p
                  className={`text-xs uppercase tracking-[0.2em] ${index % 2 === 0 ? "text-primary shadow-glow" : "text-secondary shadow-glow-secondary"} font-bold mb-3`}
                >
                  {solution.category}
                </p> */}

                {/* Title */}
                <h4 className="text-lg font-bold text-foreground group-hover:text-brand transition-colors mb-3 leading-snug">
                  {solution.title}
                </h4>

                {/* Description */}
                <p className="text-sm text-muted-foreground">
                  {solution.description}
                </p>

                {/* CTA */}
                <div
                  className={`mt-5 text-sm font-semibold ${index % 2 === 0 ? "text-primary" : "text-secondary"}`}
                >
                  View Solution →
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SolutionsMenu;
